import { NavLink, Link, useNavigate } from "react-router-dom";
import { Home, BookOpen, User, SquarePen, Image as ImageIcon, HelpCircle, Video, Settings, Wand2, Type, Sparkles,
  GitCompare, FolderOpen, ListChecks, ClipboardCheck, Database, Plug, FileCode2, Server, Bell, Search, ChevronDown, LogOut, ScrollText, Printer, Clapperboard, Feather, Sigma , Library } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useBook } from "@/context/BookContext";
import { api } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const Logo = ({ className = "" }) => (
  <Link to="/" className={`block leading-none ${className}`} data-testid="logo-link">
    <div className="font-serif-d text-[34px] font-bold tracking-tight text-navy">ZOR</div>
    <div className="font-serif-d text-[13px] text-navy -mt-0.5">Dijital Yayınevi</div>
  </Link>
);

const MAIN = [
  ["/", "Ana Kontrol Merkezi", Home], ["/kitaplar", "Kitaplar", BookOpen], ["/karakterler", "Karakterler", User],
  ["/sayfa-editoru", "Sayfa Editörü", SquarePen], ["/kapak", "Kapak", ImageIcon], ["/sorular", "Sorular", HelpCircle],
  ["/film", "Film", Video], ["/baski", "Baskı ve PDF", Printer], ["/filmler", "Film Galerisi", Clapperboard], ["/seri-rafi", "Seri Rafı", Library],
];
const STUDIO = [
  ["/ai-duzenleme", "AI Metin Düzenleme", Wand2], ["/sayfa-gorseli", "Sayfa Görseli", Sparkles], ["/dizgi", "Dizgi ve Tipografi", Type],
  ["/sahne-komutu", "Sahne Komutu", ScrollText], ["/surumler", "Öneri ve Sürümler", GitCompare], ["/varliklar", "Varlıklar ve Kuyruk", FolderOpen],
  ["/teslim-takibi", "Kurulum ve Teslim", ListChecks], ["/kabul-testleri", "Kabul Testleri", ClipboardCheck],
];
const SETTINGS = [
  ["/ayarlar", "Sistem Ayarları", Server], ["/ayarlar/api", "API Bağlantıları", Plug], ["/ayarlar/komut-sablonlari", "AI Komut Şablonları", FileCode2],
  ["/ayarlar/veri-modeli", "Veri Modeli", Database], ["/ayarlar/gelistirme", "Geliştirme Talimatları", Settings],
];

const Item = ({ to, label, Icon, small }) => (
  <NavLink to={to} end={to === "/" || to === "/ayarlar"} data-testid={`nav-${to.replace(/\//g, "-").slice(1) || "home"}`}
    className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 ${small ? "py-1.5 text-[13.5px]" : "py-2.5 text-[15px]"} transition-colors ${
      isActive ? "bg-[#e3f1f5] text-navy font-semibold border-l-[3px] border-teal" : "text-[#2d3f57] hover:bg-[#f1f6f8]"}`}>
    <Icon size={small ? 16 : 19} strokeWidth={1.6} /> <span>{label}</span>
  </NavLink>
);

export function Sidebar() {
  const [open, setOpen] = useState(true);
  return (
    <aside className="no-print w-[238px] shrink-0 border-r bg-[#fdfcfa] h-screen sticky top-0 flex flex-col" data-testid="sidebar">
      <div className="px-7 pt-5 pb-6"><Logo /></div>
      <nav className="px-2.5 space-y-0.5 overflow-y-auto scroll-thin flex-1 pb-6">
        <NavLink to="/yazar" data-testid="nav-yazar" className={({ isActive }) => `mb-3 flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] border transition-colors ${isActive ? "bg-[#13294b] text-white border-[#13294b]" : "border-[#d7e3ea] text-navy hover:bg-[#f1f6f8]"}`}>
          <Feather size={19} strokeWidth={1.6} /> <span className="font-serif-d">Yazar Paneli</span></NavLink>
        <NavLink to="/matematik" data-testid="nav-matematik" className="mb-3 flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] bg-[#13294b] text-white hover:bg-[#1d3b69] transition-colors">
          <Sigma size={19} strokeWidth={1.6} /> <span className="font-serif-d">Matematik Yazarı Paneli</span></NavLink>
        {MAIN.map(([to, l, I]) => <Item key={to} to={to} label={l} Icon={I} />)}
        <Item to="/ayarlar" label="Ayarlar" Icon={Settings} />
        <button onClick={() => setOpen(!open)} className="mt-4 mb-1 px-3 text-[11px] uppercase tracking-[0.12em] text-[#8a96a8] flex items-center gap-1" data-testid="nav-studio-toggle">
          Atölyeler <ChevronDown size={12} className={open ? "" : "-rotate-90"} />
        </button>
        {open && STUDIO.map(([to, l, I]) => <Item key={to} to={to} label={l} Icon={I} small />)}
        <div className="mt-4 mb-1 px-3 text-[11px] uppercase tracking-[0.12em] text-[#8a96a8]">Ayarlar</div>
        {SETTINGS.slice(1).map(([to, l, I]) => <Item key={to} to={to} label={l} Icon={I} small />)}
      </nav>
      <div className="px-7 py-3 border-t font-lora italic text-[#7c94a8] text-xs opacity-80 select-none">Hikâyeler daha iyi bir yarın için...</div>
    </aside>
  );
}

export function Topbar({ crumbs = [] }) {
  const { user, logout } = useAuth();
  const { book, books, setBookId } = useBook();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const doSearch = async (v) => {
    setQ(v);
    if (v.length < 2) return setRes(null);
    const { data } = await api.get("/search", { params: { q: v } });
    setRes(data);
  };
  return (
    <header className="no-print h-[56px] border-b bg-white/80 backdrop-blur sticky top-0 z-30 flex items-center px-8 gap-4">
      <div className="flex items-center gap-2 text-[14px] text-[#5f6f84] min-w-0">
        <Link to="/kitaplar" className="hover:text-navy">Projeler</Link>
        {book && (
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-navy hover:text-teal" data-testid="topbar-book-switch">
              <span className="text-[#9aa6b6]">›</span> {book.title} <ChevronDown size={14} />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Çalışılan kitap</DropdownMenuLabel>
              {books.map((b) => <DropdownMenuItem key={b.id} onClick={() => setBookId(b.id)} data-testid={`switch-book-${b.id}`}>{b.title}</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {crumbs.map((c) => <span key={c} className="text-navy truncate"><span className="text-[#9aa6b6] mr-2">›</span>{c}</span>)}
      </div>
      <div className="ml-auto relative">
        <Search size={16} className="absolute left-3 top-2.5 text-[#8a96a8]" />
        <input value={q} onChange={(e) => doSearch(e.target.value)} placeholder="Projelerde ara..." className="inp !pl-9 !py-1.5 w-[240px]" data-testid="topbar-search" />
        {res && (
          <div className="absolute top-10 left-0 w-full card-z p-2 text-sm z-40">
            {[...res.books.map((b) => ({ k: b.id, t: b.title, s: "Kitap", go: () => { setBookId(b.id); nav("/kitaplar/" + b.id); } })),
              ...res.characters.map((c) => ({ k: c.id, t: c.name, s: "Karakter", go: () => { setBookId(c.book_id); nav("/karakterler"); } }))]
              .map((x) => <button key={x.k} onClick={() => { x.go(); setRes(null); setQ(""); }} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#f1f6f8]">{x.t} <span className="text-xs text-[#8a96a8]">· {x.s}</span></button>)}
            {!res.books.length && !res.characters.length && <div className="px-2 py-1.5 text-[#8a96a8]">Sonuç yok</div>}
          </div>
        )}
      </div>
      <button className="relative text-navy" data-testid="notifications-btn" onClick={() => nav("/varliklar")}><Bell size={20} strokeWidth={1.6} /><span className="absolute -top-0.5 right-0 w-2 h-2 rounded-full bg-red-500" /></button>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1.5" data-testid="user-menu">
          <span className="w-9 h-9 rounded-full bg-teal text-white grid place-items-center text-sm font-semibold">{user?.initials || "BÖ"}</span>
          <ChevronDown size={14} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => nav("/ayarlar")}>Ayarlar</DropdownMenuItem>
          <DropdownMenuItem onClick={logout} data-testid="logout-btn"><LogOut size={14} className="mr-2" />Çıkış yap</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
